// Сессия и ядро. Здесь единственное место, где создаются транспорт, хранилище и клиент: экраны берут
// готовое и ничего не знают ни про Connect, ни про SQLite.

import {
  errorReason,
  LocalStore,
  Session,
  type SecretStore,
  SqlJsDatabase,
  YofisClient,
  createYofisTransport,
  type AuthState,
  type SessionState,
} from "@yofis/core";
import { Platform } from "@yofis/proto/yofis/v1/common_pb";
import type { NodeInfo } from "@yofis/proto/yofis/v1/node_pb";
import type { LinkPreview } from "@yofis/proto/yofis/v1/link_pb";
import type { LobbyState } from "@yofis/proto/yofis/v1/event_pb";
import type { Room } from "@yofis/proto/yofis/v1/call_pb";
import { defineStore } from "pinia";
// Путь к WebAssembly-сборке SQLite: адрес выдаёт сборщик, иначе браузер ищет файл рядом со страницей
// и локальная база молча не открывается.
import sqlWasmUrl from "sql.js/dist/sql-wasm.wasm?url";
import { ref, shallowRef } from "vue";

import { applyBranding } from "../branding.ts";
import { IdbDeviceKeyStore, IdbSecretStore } from "../platform/indexeddb.ts";
import { desktopShell, shellFetch, type ShellInfo } from "../platform/shell.ts";
import { ShellDatabase, ShellSecretStore } from "../platform/shell-storage.ts";

/** Ядро живёт рядом с сессией: пересоздавать его на каждый экран незачем. */
export const useSession = defineStore("session", () => {
  const state = ref<SessionState | undefined>(undefined);
  const node = ref<NodeInfo | undefined>(undefined);
  /** Гостевая сессия: у неё нет ни списка чатов, ни справочника — интерфейс не должен их показывать. */
  const guest = ref(false);
  /**
   * Причина последнего отказа узла (`LINK_EXPIRED`, `GUESTS_DISABLED`, …).
   *
   * Текст ошибки показывать мало: у четырёх отказов по ссылке разные следующие шаги, и экран должен
   * выбрать свой. Хранилище ловит ошибку у себя, поэтому причину оно и обязано отдать наружу —
   * иначе экран о ней не узнает и схлопнет четыре случая в один.
   */
  const reason = ref("");
  const error = ref("");
  const busy = ref(false);
  const client = shallowRef<YofisClient | undefined>(undefined);
  const session = shallowRef<Session | undefined>(undefined);

  const shell = desktopShell();
  // В вебе узел тот же, откуда открыт клиент (§4.4 архитектуры). На десктопе клиент лежит локально,
  // поэтому адрес узла запоминается отдельно — и это единственный адрес, к которому оболочка ходит.
  // Адрес узла изменяем: на десктопе его называет человек, и экран должен смениться сразу, а не после
  // перезапуска приложения.
  const baseUrl = ref(shell === undefined ? window.location.origin : (savedNodeUrl() ?? ""));
  // Секреты: в браузере — IndexedDB (хранит `CryptoKey` объектом), на десктопе — хранилище ключей системы.
  const secrets: SecretStore = shell === undefined ? new IdbSecretStore() : new ShellSecretStore(shell);
  const keys = new IdbDeviceKeyStore();
  const shellInfo = ref<ShellInfo | undefined>(undefined);
  /** Алгоритм подписи этого устройства: показывается человеку и пишется в журнал. */
  const algorithm = ref<string | undefined>(undefined);


  /** Сеть: в браузере обычная, на десктопе — через оболочку (у рендерера прямого выхода нет). */
  function net(): typeof fetch | undefined {
    return shell === undefined ? undefined : shellFetch(shell);
  }

  /** Локальная база: в браузере SQLite в WebAssembly, на десктопе — файл с шифрованием страниц. */
  async function openStore(): Promise<LocalStore> {
    if (shell !== undefined) {
      return LocalStore.open(await ShellDatabase.open(shell));
    }
    return LocalStore.open(await SqlJsDatabase.open({ locateFile: () => sqlWasmUrl }));
  }

  function ensure(): Session {
    if (session.value !== undefined) {
      return session.value;
    }
    const auth = { current: (): AuthState => session.value?.current() ?? {} };
    const fetchImpl = net();
    const transport = createYofisTransport({
      baseUrl: baseUrl.value,
      auth,
      client: shell === undefined ? "yofis-web/0.1.0" : "yofis-desktop/0.1.0",
      ...(fetchImpl === undefined ? {} : { fetch: fetchImpl }),
    });
    const created = new Session({
      transport,
      secrets,
      keys,
      device: { name: deviceName(), platform: Platform.WEB, appVersion: "0.1.0" },
    });
    session.value = created;
    return created;
  }

  /**
   * Запоминает и объявляет алгоритм подписи. Ed25519 закреплён намеренно (ADR-031); если среда его не
   * дала и ядро ушло на запасной P-256, это должно быть видно человеку и в журнале, а не остаться
   * молчаливым понижением.
   */
  function noteAlgorithm(): void {
    const chosen = session.value?.algorithm;
    algorithm.value = chosen;
    if (chosen === "ECDSA_P256") {
      shell?.log("warn", "ключ устройства создан запасным алгоритмом P-256: Ed25519 в этой среде недоступен");
    } else if (chosen !== undefined) {
      shell?.log("info", `ключ устройства: ${chosen}`);
    }
  }

  /** Сведения об оболочке: версия, система и состояние хранилища ключей. В браузере их нет. */
  async function loadShell(): Promise<void> {
    if (shell === undefined) {
      return;
    }
    shellInfo.value = await shell.info();
    if (baseUrl.value !== "") {
      await shell.net.setNode(baseUrl.value);
    }
  }

  /** Сведения об узле до входа: название, оформление, доступные способы входа. */
  async function loadNode(): Promise<void> {
    node.value = await ensure().nodeInfo();
    applyBranding(node.value);
  }

  async function start(opened: SessionState): Promise<void> {
    state.value = opened;
    noteAlgorithm();
    const fetchImpl = net();
    const transport = createYofisTransport({
      baseUrl: baseUrl.value,
      auth: { current: (): AuthState => ensure().current() },
      client: shell === undefined ? "yofis-web/0.1.0" : "yofis-desktop/0.1.0",
      ...(fetchImpl === undefined ? {} : { fetch: fetchImpl }),
    });
    const store = await openStore();
    const created = new YofisClient({ transport, store, me: opened.address });
    client.value = created;
    await created.reload();
    created.start();
  }

  async function login(loginName: string, password: string): Promise<void> {
    busy.value = true;
    error.value = "";
    reason.value = "";
    try {
      await start(await ensure().loginWithPassword(loginName, password));
    } catch (err) {
      error.value = describe(err);
      reason.value = errorReason(err) ?? "";
    } finally {
      busy.value = false;
    }
  }

  /** Что за ссылкой — до того, как предлагать человеку что бы то ни было. */
  async function resolveLink(token: string): Promise<LinkPreview | undefined> {
    return ensure().resolveLink(token);
  }

  /** Регистрация сотрудника по приглашению; пароль с 0.15 необязателен. */
  async function join(token: string, displayName: string, password = ""): Promise<void> {
    busy.value = true;
    error.value = "";
    reason.value = "";
    try {
      await start(await ensure().registerByInvite(token, displayName, password));
    } catch (err) {
      error.value = describe(err);
      reason.value = errorReason(err) ?? "";
    } finally {
      busy.value = false;
    }
  }

  /** Вход существующего сотрудника по ссылке — без пароля (0.15). */
  async function loginByLink(token: string): Promise<void> {
    busy.value = true;
    error.value = "";
    reason.value = "";
    try {
      await start(await ensure().loginWithLink(token));
    } catch (err) {
      error.value = describe(err);
      reason.value = errorReason(err) ?? "";
    } finally {
      busy.value = false;
    }
  }

  /**
   * Вход внешнего участника во встречу: только имя.
   *
   * Гостю доступны девять методов узла, и полной загрузки среди них нет — поэтому здесь НЕ вызывается
   * `start()`: он тянет список чатов, которого гость не получит, и человек увидел бы ошибку на пустом
   * месте вместо встречи.
   */
  async function joinAsGuest(token: string, displayName: string): Promise<void> {
    busy.value = true;
    error.value = "";
    reason.value = "";
    try {
      state.value = await ensure().joinAsGuest(token, displayName);
      noteAlgorithm();
      guest.value = true;
    } catch (err) {
      error.value = describe(err);
      reason.value = errorReason(err) ?? "";
    } finally {
      busy.value = false;
    }
  }

  /**
   * Вход во встречу по ссылке с ожиданием в лобби.
   *
   * События слушаются без локальной базы: у гостя нет истории, и заводить ради ожидания базу
   * и синхронизацию значило бы обещать ему клиент, которого он не получит.
   */
  async function joinRoomByLink(
    token: string,
    signal: AbortSignal,
    onLobby: (state: LobbyState) => void,
  ): Promise<{ room: Room | undefined; lobbyPending: boolean }> {
    const entered = await ensure().joinByLink(token);
    if (entered.lobbyPending) {
      ensure().watchEvents((event) => {
        if (event.payload.case === "lobbyUpdated") {
          onLobby(event.payload.value.state);
        }
      }, signal);
    }
    return { room: entered.room, lobbyPending: entered.lobbyPending };
  }

  /** Восстановление после перезагрузки вкладки: токен и ключ лежат в IndexedDB. */
  async function restore(): Promise<boolean> {
    const s = ensure();
    if (!(await s.restore())) {
      return false;
    }
    // Профиль спрашиваем у узла: локально его нет, а показывать чужое имя из кеша нельзя.
    const fetchImpl = net();
    const transport = createYofisTransport({
      baseUrl: baseUrl.value,
      auth: { current: (): AuthState => s.current() },
      client: shell === undefined ? "yofis-web/0.1.0" : "yofis-desktop/0.1.0",
      ...(fetchImpl === undefined ? {} : { fetch: fetchImpl }),
    });
    const store = await openStore();
    const probe = new YofisClient({ transport, store, me: "" });
    const user = await probe.profile().catch(() => undefined);
    if (user === undefined) {
      // Токен протух или устройство отозвали — показываем вход, а не пустой экран.
      return false;
    }
    noteAlgorithm();
    state.value = {
      address: user.address,
      displayName: user.displayName,
      deviceId: s.current().deviceId ?? "",
      expiresAt: undefined,
      node: undefined,
    };
    // Теперь адрес известен: без него свои сообщения выглядели бы чужими.
    probe.me = user.address;
    client.value = probe;
    await probe.reload();
    probe.start();
    return true;
  }

  async function logout(): Promise<void> {
    client.value?.stop();
    client.value = undefined;
    await session.value?.logout();
    state.value = undefined;
  }

  /** Адрес узла на десктопе: спрашивается один раз и запоминается. */
  async function setNode(url: string): Promise<void> {
    const origin = new URL(url).origin;
    // Сессию пересоздаём: транспорт строится от адреса, а он только что изменился.
    session.value = undefined;
    baseUrl.value = origin;
    await shell?.net.setNode(origin);
    try {
      await loadNode();
      rememberNodeUrl(origin);
    } catch (err) {
      // Узел не ответил — адрес не запоминаем: иначе следующий запуск упрётся в тот же неверный адрес.
      baseUrl.value = "";
      throw err;
    }
  }

  return {
    state, node, error, busy, client, shell, shellInfo, baseUrl, guest, reason,
    algorithm, loadShell, loadNode, login, join, logout, restore, setNode,
    resolveLink, loginByLink, joinAsGuest, joinRoomByLink,
  };
});

/** Адрес узла на десктопе: в браузере он известен из адресной строки, здесь его называет человек. */
function savedNodeUrl(): string | undefined {
  try {
    return localStorage.getItem("yofis.node") ?? undefined;
  } catch {
    return undefined;
  }
}

function rememberNodeUrl(origin: string): void {
  try {
    localStorage.setItem("yofis.node", origin);
  } catch {
    // Приватный режим: адрес придётся ввести снова при следующем запуске.
  }
}

/** Имя устройства в списке сессий: браузер и система, без подробностей, которые ничего не говорят. */
function deviceName(): string {
  const ua = navigator.userAgent;
  const browser = /Firefox\/\d+/.exec(ua)?.[0] ?? /Chrome\/\d+/.exec(ua)?.[0] ?? /Safari\/\d+/.exec(ua)?.[0] ?? "Браузер";
  return `${browser.split("/")[0] ?? "Браузер"} · веб`;
}

function describe(err: unknown): string {
  if (err instanceof Error) {
    // Connect кладёт понятное сообщение сервера в текст ошибки — показываем его, а не «что-то пошло не так».
    return err.message.replace(/^\[[a-z_]+]\s*/, "");
  }
  return String(err);
}
